<template>
    <section v-cloak>
        <div class="container">
            <div v-if="hasPermission">
                <div v-if="model" class="space-y-12">
                    <section>
                        <locations-map :zetels="model.maatschappelijkeAdressen" :vestigingen="model.vestigingen" :bijkantoren="model.bijkantoren" :postbus-adressen="model.mailboxAdressen"></locations-map>
                    </section>
                    
                    <section v-if="model.maatschappelijkeAdressen.length > 0">
                        <div>
                            <h2 class="font-medium text-md mobile:text-base">
                                {{ translate('maatschappelijke_adressen') }}
                            </h2>

                            <table class="table-responsive">
                                <thead>
                                    <tr>
                                        <th scope="col">{{ translate('adres') }}</th>
                                        <th scope="col" v-if="hasSiret">Siret</th>
                                        <th width="250" scope="col">{{ translate('periode') }}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="mztl in model.maatschappelijkeAdressen">
                                        <td>
                                            <adres :address="mztl.adres" :is-adres-doorgehaald="mztl.isAdresDoorgehaald" :adres-doorgehaald-datum="mztl.adresDoorgehaaldDatum"></adres>
                                        </td>
                                        <td v-if="hasSiret && (mztl.einde == 0 || mztl.einde == null)">
                                            {{detailStore.siret}}
                                        </td>
                                        <td>
                                            <mandate-timeline
                                                :mandates="[{status: (!!mztl.einde ? MandateStatus.MandateEnded : MandateStatus.MandateIsActive), begin:mztl.begin, einde: mztl.einde}]"
                                                :company-start-date="detailStore.companyBegin"
                                                :company-end-date="detailStore.companyEnd"></mandate-timeline>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </section>
                    
                    <section v-if="detailStore.country !== 'nl' || model.vestigingen.length > 0">
                        <h2 class="font-medium text-md mobile:text-base">
                            {{ detailStore.country === "nl" ? translate('nevenvestigingen') : translate('vestigingen') }}
                        </h2>
                        <template v-if="model.vestigingen.length > 0">
                            <table class="table-responsive">
                                <thead>
                                <tr class="title-tab">
                                    <th scope="col">{{ translate('adres') }}</th>
                                    <th class="tablet:hidden" scope="col" width="250">{{ translate('naam') }}</th>
                                    <th scope="col" class="w-40">{{ hasSiret ? 'Siret' : translate('vestigingsnummer') }}</th>
                                    <th scope="col" class="w-64">{{ translate('periode') }}</th>
                                </tr>
                                </thead>
                                <tbody>
                                <tr v-for="vestiging in model.vestigingen">
                                    <td>
                                        <adres :address="vestiging.adres" :adres-doorgehaald-datum="vestiging.adresDoorgehaaldDatum" :is-adres-doorgehaald="vestiging.isAdresDoorgehaald"></adres>
                                    </td>
                                    <td class="font-bold tablet:hidden">
                                        {{ vestiging.name ? vestiging.name : detailStore.naamOfAfkorting }}
                                    </td>
                                    <td>
                                        {{ vestiging.nummer ?? vestiging.altaresNummer }}
                                    </td>
                                    <td>
                                        <mandate-timeline :mandates="[{status:MandateStatus.MandateIsActive, begin:vestiging.begin, einde: null}]"
                                                          :company-start-date="detailStore.companyBegin"
                                                          :company-end-date="detailStore.companyEnd"></mandate-timeline>
                                    </td>
                                </tr>
                                </tbody>
                            </table>
                        </template>
                        <p v-else class="text-grey-700">
                            {{translate('geen_actieve_vestigingen')}}
                        </p>
                    </section>

                    <section v-if="model.bijkantoren.length > 0">
                        <h2 class="font-medium text-md mobile:text-base">
                            {{ translate('bijkantoren') }}
                        </h2>
                        <table  class="table-responsive">
                            <thead>
                            <tr>
                                <th scope="col">{{ translate('adres') }}</th>
                                <th scope="col" width="250">{{ translate('naam') }}</th>
                                <th scope="col" width="250">{{ translate('periode') }}</th>
                            </tr>
                            </thead>
                            <tbody>
                            <tr v-for="vestiging in model.bijkantoren">
                                <td>
                                    <adres :address="vestiging.adres" :adres-doorgehaald-datum="vestiging.adresDoorgehaaldDatum" :is-adres-doorgehaald="vestiging.isAdresDoorgehaald"></adres>
                                </td>
                                <td class="font-bold">
                                    {{ vestiging.name ? vestiging.name : detailStore.naamOfAfkorting }}
                                </td>
                                <td>
                                    <mandate-timeline :mandates="[{status: MandateStatus.MandateIsActive, begin: vestiging.begin, einde: null}]"
                                                      :company-start-date="detailStore.companyBegin"
                                                      :company-end-date="detailStore.companyEnd"></mandate-timeline>
                                </td>
                            </tr>
                            </tbody>
                        </table>
                    </section>
                    
                    <section v-if="model.mailboxAdressen.length > 0">
                        <h2 class="font-medium text-md mobile:text-base">
                            Postbus adressen
                        </h2> 
                        <ul>
                            <li v-for="adres in model.mailboxAdressen" class="flex items-center gap-2.5">
                                <i class="fas fa-envelope text-grey-500"></i> {{adres.lijn2}} {{translate('bus').toLowerCase()}} {{adres.busNummer}}
                            </li>
                        </ul>
                    </section>
                </div>
            </div>
            <div v-else>
                <no-access :sub-page="detailStore.SubPage.LOCATIONS"></no-access>
            </div>
        </div>
    </section>
</template>

<script lang="ts">
import {useDetailStore} from "../store";
import {LocationsModel, MandateStatus, SubPage} from "../types";
import {defineComponent} from 'vue';
import Adres from "../../../components/adres.vue";
import MandateTimeline from "../../../components/mandate-timeline.vue";
import NoAccess from "./no-access.vue";
import {Feature} from "../../../types/dto/Feature";
import LocationsMap from "./locations/locations-map.vue";

export default defineComponent({
    name: "locations",
    components: {
        LocationsMap,
        NoAccess,
        MandateTimeline,
        Adres
    },
    data() {
        return {
            detailStore: useDetailStore(),
            MandateStatus,
            Feature
        }
    },
    computed: {
        hasPermission(): boolean {
            return this.detailStore.canSeePaidComponents;
        },
        model(): LocationsModel {
            return this.detailStore.pageModel[SubPage.LOCATIONS]!;
        },
        hasSiret(): boolean {
            return this.detailStore.siret !== null && this.detailStore.siret !== '';
        }
    }
});
</script>

